import { useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Container, Image, Row, Spinner, Table } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';

import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import Meta from '../../components/layout/Meta';
import BrandFormModal from '../../components/admin/BrandFormModal';
import { useDeleteBrandMutation, useGetBrandsQuery } from '../../slices/brandApiSlice';
import useIsMobile from '../../utils/useIsMobile';

const BrandListScreen = () => {
    const isMobile = useIsMobile();

    const {
        data: brands = [],
        isLoading,
        isFetching,
        error,
        refetch,
    } = useGetBrandsQuery();

    const [deleteBrand, { isLoading: isDeleting }] = useDeleteBrandMutation();

    const [showModal, setShowModal] = useState(false);
    const [initialData, setInitialData] = useState(null);

    const visibleBrands = useMemo(() => {
        if (!Array.isArray(brands)) return [];
        return [...brands].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'es')
        );
    }, [brands]);

    const openCreateModal = () => {
        setInitialData(null);
        setShowModal(true);
    };

    const openEditModal = (brand) => {
        setInitialData(brand);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setInitialData(null);
    };

    const handleDelete = async (brandId, brandName) => {
        if (!window.confirm(`¿Seguro que deseas eliminar la marca "${brandName}"?`)) return;
        try {
            await deleteBrand(brandId).unwrap();
            toast.success('Marca eliminada correctamente.');
        } catch (err) {
            toast.error(err?.data?.message || err?.error || 'No se pudo eliminar la marca.');
        }
    };

    const errorMessage = error?.data?.message || error?.error;

    return (
        <Container fluid>
            <Meta title="Marcas" />

            {/* Header */}
            <Row className="align-items-center mb-3">
                <Col>
                    <h1 className="mb-0">Marcas</h1>
                    <small className="text-muted">Gestiona las marcas de productos de la tienda.</small>
                </Col>
                <Col xs="auto">
                    <Button
                        onClick={openCreateModal}
                        style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}
                        className="d-inline-flex align-items-center gap-2"
                    >
                        <FaPlus /> Nueva marca
                    </Button>
                </Col>
            </Row>

            {/* Loading / Error */}
            {isLoading && visibleBrands.length === 0 ? <Loader /> : null}
            {errorMessage ? <Message variant="danger">{errorMessage}</Message> : null}

            {/* Empty state */}
            {!isLoading && !isFetching && visibleBrands.length === 0 && !errorMessage ? (
                <Card className="shadow-sm border-0">
                    <Card.Body className="text-center py-5 text-muted">
                        No hay marcas registradas todavía.
                    </Card.Body>
                </Card>
            ) : null}

            {/* Desktop table */}
            {visibleBrands.length > 0 && !isMobile ? (
                <Card className="shadow-sm border-0">
                    <Card.Body className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr>
                                    <th style={{ width: '72px' }}>Logo</th>
                                    <th>Nombre</th>
                                    <th>Descripción</th>
                                    <th className="text-center">Estado</th>
                                    <th className="text-end">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleBrands.map((brand) => (
                                    <tr key={brand._id}>
                                        <td>
                                            {brand.image ? (
                                                <Image
                                                    src={brand.image}
                                                    alt={brand.name}
                                                    thumbnail
                                                    style={{
                                                        width: '48px',
                                                        height: '48px',
                                                        objectFit: 'contain',
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="rounded border d-flex align-items-center justify-content-center text-muted"
                                                    style={{
                                                        width: '48px',
                                                        height: '48px',
                                                        fontSize: '0.65rem',
                                                        backgroundColor: '#fdf5f2',
                                                    }}
                                                >
                                                    Sin img
                                                </div>
                                            )}
                                        </td>
                                        <td className="fw-semibold">{brand.name}</td>
                                        <td className="text-muted" style={{ maxWidth: '320px' }}>
                                            <span
                                                className="d-inline-block text-truncate"
                                                style={{ maxWidth: '300px' }}
                                                title={brand.description}
                                            >
                                                {brand.description || '—'}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            {brand.isActive ? (
                                                <Badge bg="success">Activa</Badge>
                                            ) : (
                                                <Badge bg="secondary">Inactiva</Badge>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <div className="d-inline-flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={() => openEditModal(brand)}
                                                    aria-label={`Editar ${brand.name}`}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={() => handleDelete(brand._id, brand.name)}
                                                    aria-label={`Eliminar ${brand.name}`}
                                                    disabled={isDeleting}
                                                >
                                                    {isDeleting ? (
                                                        <Spinner animation="border" size="sm" />
                                                    ) : (
                                                        <FaTrash />
                                                    )}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            ) : null}

            {/* Mobile cards */}
            {visibleBrands.length > 0 && isMobile ? (
                <div>
                    {visibleBrands.map((brand) => (
                        <Card key={brand._id} className="mb-3 shadow-sm border-0 rounded-3">
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    {/* Left: image + name */}
                                    <div className="d-flex align-items-center gap-3">
                                        {brand.image ? (
                                            <Image
                                                src={brand.image}
                                                alt={brand.name}
                                                thumbnail
                                                style={{ width: '52px', height: '52px', objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <div
                                                className="rounded border d-flex align-items-center justify-content-center text-muted"
                                                style={{
                                                    width: '52px',
                                                    height: '52px',
                                                    fontSize: '0.6rem',
                                                    backgroundColor: '#fdf5f2',
                                                }}
                                            >
                                                Sin img
                                            </div>
                                        )}
                                        <div>
                                            <div
                                                className="fw-bold fs-6"
                                                style={{ color: '#b5430f' }}
                                            >
                                                {brand.name}
                                            </div>
                                            {brand.isActive ? (
                                                <Badge bg="success" className="mt-1">Activa</Badge>
                                            ) : (
                                                <Badge bg="secondary" className="mt-1">Inactiva</Badge>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right: actions */}
                                    <div className="d-flex gap-2 flex-shrink-0">
                                        <Button
                                            size="sm"
                                            variant="light"
                                            className="border"
                                            onClick={() => openEditModal(brand)}
                                            aria-label={`Editar ${brand.name}`}
                                        >
                                            <FaEdit />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="light"
                                            className="border border-danger text-danger"
                                            onClick={() => handleDelete(brand._id, brand.name)}
                                            aria-label={`Eliminar ${brand.name}`}
                                            disabled={isDeleting}
                                        >
                                            {isDeleting ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                <FaTrash />
                                            )}
                                        </Button>
                                    </div>
                                </div>

                                {brand.description ? (
                                    <p className="text-muted small mb-0">{brand.description}</p>
                                ) : null}
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            ) : null}

            <BrandFormModal
                show={showModal}
                handleClose={closeModal}
                initialData={initialData}
                onSuccess={refetch}
            />
        </Container>
    );
};

export default BrandListScreen;

